// src/modules/app-views/account/pages/Subscription/modals/DepositSparksModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useState } from 'react';
import { Button, Col, Descriptions, Form, Input, InputNumber, Row, Select, Space, Table, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import translateX from 'utils/translateX';

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const DepositSparksStepContent = ({ record, currentBalance, operation, onOperationChange, movements, onConfirm }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const form = Form.useFormInstance();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE (ANTD v6: controlled open state for Select + pagination)
  // ─────────────────────────────────────────────────────────────────────────────
  const [openSelect, setOpenSelect] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Jump to last page whenever a new movement is added
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(movements.length / pageSize));
    setCurrentPage(lastPage);
  }, [movements.length]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // ANTD v6 fix: controlled open state ensures dropdown closes on first click
  const handleSelect = useCallback(() => setOpenSelect(false), []);
  const handleOpenChange = useCallback(visible => setOpenSelect(visible), []);

  const handleConfirmClick = useCallback(async () => {
    try {
      const values = await form.validateFields(['amount', 'description']);
      onConfirm(values);
      form.resetFields(['amount', 'description']);
    } catch (e) {
      // Show validation errors and scroll/focus the first invalid field
      if (e?.errorFields?.length) {
        form.scrollToField(e.errorFields[0].name, { behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [form, onConfirm]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = buildColumns();

  const descriptionItems = [
    {
      key: 'user',
      label: translateX('user'),
      children: record?.name || '—',
    },
    {
      key: 'balance',
      label: translateX('spark_coins_balance'),
      children: <strong style={{ color: currentBalance > 0 ? '#d48806' : undefined }}>{currentBalance}</strong>,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* User + Balance summary */}
      <Descriptions
        size="small"
        column={{ xs: 1, sm: 2 }}
        style={{ marginBottom: 16, background: '#fafafa', padding: '10px 14px', borderRadius: 8 }}
        items={descriptionItems}
      />

      {/* Deposit form row */}
      <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 16 }}>
        <Col flex="auto" style={{ minWidth: 220 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Select
              style={{ width: 130 }}
              value={operation}
              open={openSelect}
              onOpenChange={handleOpenChange}
              onSelect={handleSelect}
              onChange={onOperationChange}
              options={[
                { value: 'credit', label: translateX('credit') },
                { value: 'debit', label: translateX('debit') },
              ]}
            />
            <Form.Item
              name="amount"
              noStyle
              rules={[{ required: true, message: () => translateX('required_field') }]}
            >
              <InputNumber min={1} style={{ flex: 1, width: '100%' }} />
            </Form.Item>
            <Input
              style={{
                width: 72,
                pointerEvents: 'none',
                background: '#fafafa',
                textAlign: 'center',
                cursor: 'default',
              }}
              value="Sparks"
              readOnly
            />
          </Space.Compact>
        </Col>

        <Col flex="1 1 180px">
          <Form.Item
            name="description"
            noStyle
            rules={[{ required: true, message: () => translateX('required_field') }]}
          >
            <Input placeholder={translateX('description')} />
          </Form.Item>
        </Col>

        <Col flex="none">
          <Tooltip title={translateX('confirm')}>
            <Button type="primary" icon={<CheckOutlined />} onClick={handleConfirmClick} />
          </Tooltip>
        </Col>
      </Row>

      {/* Movements history table — rootClassName scopes the CSS override to this table only */}
      <style>{`.deposit-sparks-table .ant-table-content { overflow: hidden !important; }`}</style>
      <Table
        size="small"
        rowKey="_id"
        rootClassName="deposit-sparks-table"
        columns={columns}
        dataSource={movements}
        pagination={{
          pageSize,
          current: currentPage,
          onChange: setCurrentPage,
          hideOnSinglePage: true,
        }}
        locale={{ emptyText: ' ' }}
      />
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const depositSparksStep = ({ record, currentBalance, operation, onOperationChange, movements, onConfirm }) => ({
  stepTitle: 'deposit_spark_coins',
  nextButtonText: 'close',
  nextButtonProps: { type: 'default', icon: <CloseOutlined /> },
  content: () => (
    <DepositSparksStepContent
      record={record}
      currentBalance={currentBalance}
      operation={operation}
      onOperationChange={onOperationChange}
      movements={movements}
      onConfirm={onConfirm}
    />
  ),
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default depositSparksStep;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildColumns() {
  return [
    {
      title: translateX('date'),
      dataIndex: 'createdAt',
      width: 120,
      render: v => dayjs(v).format('DD/MM/YY HH:mm'),
    },
    {
      title: translateX('description'),
      dataIndex: 'description',
      render: (text, row) => (
        <div>
          <div>{text}</div>
          {row.userName && (
            <div style={{ color: '#bfbfbf', fontSize: 11, marginTop: 2 }}>por {row.userName}</div>
          )}
        </div>
      ),
    },
    {
      title: translateX('spark_coins'),
      dataIndex: 'delta',
      width: 90,
      align: 'center',
      render: v => (
        <span style={{ color: v < 0 ? '#ff4d4f' : undefined, fontWeight: 600 }}>
          {v < 0 ? v : `+${v}`}
        </span>
      ),
    },
    {
      title: translateX('spark_coins_balance'),
      dataIndex: 'balance',
      width: 110,
      align: 'center',
    },
  ];
}
